import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Pin, PinOff, Plus, StickyNote, Trash2 } from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  createNote,
  listNotes,
  listProperties,
  removeNote,
  updateNote,
} from "@/services/dataService";
import { formatDate } from "@/lib/format";

const EMPTY_FORM = { title: "", body: "", propertyId: "" };

export default function Notes() {
  const { currentUser } = useAuth();
  const userId = currentUser?._id;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const notesQuery = useQuery({
    queryKey: ["quickNotes", userId],
    queryFn: () => listNotes(),
    enabled: Boolean(userId),
  });
  const propertiesQuery = useQuery({
    queryKey: ["properties", userId],
    queryFn: () => listProperties(),
    enabled: Boolean(userId),
  });

  const propertyName = useMemo(() => {
    const map = new Map();
    for (const property of propertiesQuery.data || []) {
      map.set(String(property._id), property.name || property.address);
    }
    return map;
  }, [propertiesQuery.data]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["quickNotes", userId] });

  const saveMutation = useMutation({
    mutationFn: (payload) => (editing ? updateNote(payload) : createNote(payload)),
    onSuccess: async () => {
      await invalidate();
      closeDialog();
      toast({ title: editing ? "Note updated" : "Note saved" });
    },
    onError: (error) =>
      toast({ title: "Could not save", description: error.message, variant: "destructive" }),
  });

  const pinMutation = useMutation({
    mutationFn: ({ id, pinned }) => updateNote({ id, pinned }),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: removeNote,
    onSuccess: invalidate,
    onError: (error) =>
      toast({ title: "Could not delete", description: error.message, variant: "destructive" }),
  });

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  }
  function openEdit(note) {
    setEditing(note);
    setForm({
      title: note.title || "",
      body: note.body || "",
      propertyId: note.propertyId ? String(note.propertyId) : "",
    });
    setOpen(true);
  }
  function closeDialog() {
    setOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  }

  function handleSave() {
    const base = {
      title: form.title.trim(),
      body: form.body.trim(),
    };
    if (editing) {
      saveMutation.mutate({
        id: editing._id,
        ...base,
        propertyId: form.propertyId || undefined,
        clearProperty: !form.propertyId,
      });
    } else {
      saveMutation.mutate({ ...base, propertyId: form.propertyId || undefined });
    }
  }

  const notes = notesQuery.data || [];

  return (
    <PageLayout
      title="Notes"
      eyebrow="Scratchpad"
      subtitle="Reminders, to-dos, and anything else you used to jot on the Notes sheet."
      onRefresh={() => notesQuery.refetch()}
      isRefreshing={notesQuery.isFetching}
    >
      <div className="space-y-6">
        <div className="flex justify-end">
          <Button onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            New note
          </Button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {notes.map((note) => (
            <Card key={note._id} className={note.pinned ? "ring-1 ring-amber-400/40" : undefined}>
              <CardContent className="flex h-full flex-col gap-3 p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold leading-tight">{note.title || "Untitled"}</h3>
                  <button
                    type="button"
                    onClick={() => pinMutation.mutate({ id: note._id, pinned: !note.pinned })}
                    className={
                      note.pinned
                        ? "text-amber-500"
                        : "text-muted-foreground transition hover:text-foreground"
                    }
                    aria-label={note.pinned ? "Unpin note" : "Pin note"}
                  >
                    {note.pinned ? <Pin className="h-4 w-4" /> : <PinOff className="h-4 w-4" />}
                  </button>
                </div>
                <p className="flex-1 whitespace-pre-wrap text-sm text-muted-foreground">
                  {note.body}
                </p>
                {note.propertyId ? (
                  <Badge variant="outline" className="w-fit gap-1">
                    <Building2 className="h-3 w-3" />
                    {propertyName.get(String(note.propertyId)) || "Property"}
                  </Badge>
                ) : null}
                <div className="flex items-center justify-between border-t pt-2 text-xs text-muted-foreground">
                  <span>{formatDate(note.updatedAt)}</span>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => openEdit(note)}>
                      Edit
                    </Button>
                    <button
                      type="button"
                      onClick={() =>
                        window.confirm("Delete this note?") && deleteMutation.mutate(note._id)
                      }
                      className="text-muted-foreground transition hover:text-rose-600"
                      aria-label="Delete note"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {!notesQuery.isLoading && notes.length === 0 ? (
            <Card className="sm:col-span-2 lg:col-span-3">
              <CardContent className="py-14 text-center">
                <StickyNote className="mx-auto h-10 w-10 text-muted-foreground/40" />
                <p className="mt-3 font-medium">No notes yet</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Jot down anything you want to remember about the portfolio.
                </p>
                <Button onClick={openCreate} className="mt-4 gap-2">
                  <Plus className="h-4 w-4" />
                  New note
                </Button>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>

      <Dialog open={open} onOpenChange={(value) => (value ? setOpen(true) : closeDialog())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit note" : "New note"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="space-y-1">
              <Label>Title</Label>
              <Input
                value={form.title}
                onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Note</Label>
              <textarea
                rows={6}
                value={form.body}
                onChange={(event) => setForm((prev) => ({ ...prev, body: event.target.value }))}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label>Property (optional)</Label>
              <select
                value={form.propertyId}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, propertyId: event.target.value }))
                }
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              >
                <option value="">Portfolio-level</option>
                {(propertiesQuery.data || []).map((property) => (
                  <option key={property._id} value={property._id}>
                    {property.name || property.address}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saveMutation.isPending || (!form.title.trim() && !form.body.trim())}
            >
              {saveMutation.isPending ? "Saving…" : "Save note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
